import React, { useEffect, useMemo } from "react";
import { commitMutation, graphql } from "react-relay";

import { faCheck, faLink, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import {
	faAngleDown,
	faAngleUp,
	faArrowUp,
	faEllipsisV,
	faTimes,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import { ALIGN_OPTIONS, SIDE_OPTIONS } from "@radix-ui/react-popper";
import cx from "classnames";
import { cloneDeep, throttle } from "lodash";
import { Node as ProseNode } from "prosemirror-model";
import useResizeObserver from "use-resize-observer";
import uuid from "uuid";

import { showFeatureModal, UpgradeIcon } from "~/billing";
import AppContext from "~/common/AppContext";
import { confirm } from "~/lib/prompts";
import { EditorConfig, ProseReact } from "~/lib/prose";
import { resetState, TypeAheadPluginKey } from "~/lib/prose/plugins/typeahead";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { nGetText } from "~/lib/utils";
import { VirtualKeyboardDetector } from "~/lib/virtualKeyboard";
import WebsocketClient from "~/lib/websocket";
import TranscriptCommentsRecipientSelector, {
	type CommentRecipientUserType,
} from "~/meetingRecorder/TranscriptCommentsRecipientSelector";
import { environment } from "~/models";
import {
	Comment,
	type CommentOperationWebsocketMessage,
	createOrUpdateComment,
	reopenThreadMutation,
	resolveThreadMutation,
	threadsQuery,
} from "~/stream/Comments";
import CommentsEditor from "~/stream/Comments/commentsEditor";
import { threadKeyFromRecapIdAndTimes } from "~/stream/Comments/threadKeys";
import { Avatar, Button } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Menu from "~/ui/menu";
import toast, { ToastActionButton } from "~/ui/toast";

import { copyToClipboard } from "./utils";

import type { CommentThreadAccessType } from "~/__generated__/CommentsCreateOrUpdateCommentMutation.graphql";
import type { CommentsQuery } from "~/__generated__/CommentsQuery.graphql";
import type { CommentsReopenThreadMutation } from "~/__generated__/CommentsReopenThreadMutation.graphql";
import type { CommentsResolveThreadMutation } from "~/__generated__/CommentsResolveThreadMutation.graphql";
import type { TranscriptCommentsPopoverDeleteThreadMutation } from "~/__generated__/TranscriptCommentsPopoverDeleteThreadMutation.graphql";

import styles from "./css/TranscriptCommentsPopover.module.css";

const EMPTY_DOC = {
	type: "doc",
	content: [
		{
			type: "unstyled",
			attrs: {
				assignees: [],
				creator: null,
				blockId: uuid(),
				indent: 0,
				done: false,
			},
		},
	],
};

const deleteThreadMutation = graphql`
	mutation TranscriptCommentsPopoverDeleteThreadMutation($id: ID!) {
		Comment_DeleteThread(threadId: $id) {
			ok
		}
	}
`;

type TranscriptCommentsPopoverProps = {
	noteId: string;
	organizerId?: string;
	recordingId: string;
	selectedCommentTimestampStart: number;
	selectedCommentTimestampEnd: number;
	closeCommentPanel: () => void;
	noteUsers: Array<CommentRecipientUserType>;
	hasAccessToRecapComments: boolean;
	hasAccessToPrivateRecapComments: boolean;
	narrowViewport?: boolean;
	uxSimplificationEnabled?: boolean;
};

function TranscriptCommentsPopover({
	noteId,
	organizerId,
	recordingId,
	closeCommentPanel,
	selectedCommentTimestampStart,
	selectedCommentTimestampEnd,
	noteUsers,
	hasAccessToRecapComments,
	hasAccessToPrivateRecapComments,
}: TranscriptCommentsPopoverProps) {
	const [content, setContent] = React.useState<ProseNode | { [key: string]: unknown }>(EMPTY_DOC);
	const editorConfigRef = React.useRef<EditorConfig>();
	const editorRef = React.useRef<ProseReact>(null);
	const virtualKeyboardDetector = React.useMemo(() => new VirtualKeyboardDetector(), []);
	const [isLoading, setIsLoading] = React.useState(false);
	const [actionLoading, setActionLoading] = React.useState(false);
	const [clearCount, setClearCount] = React.useState(0);
	const { user } = React.useContext(AppContext);
	const threadKey = React.useMemo(
		() => threadKeyFromRecapIdAndTimes(recordingId, selectedCommentTimestampStart, selectedCommentTimestampEnd),
		[recordingId, selectedCommentTimestampStart, selectedCommentTimestampEnd],
	);
	const [newCommentAccessType, setNewCommentAccessType] = React.useState<CommentThreadAccessType>("EVERYONE");
	const [newCommentRecipientUser, setNewCommentRecipientUser] = React.useState<CommentRecipientUserType | undefined>();
	const [currentThreads, setCurrentThreads] = React.useState<CommentsQuery["response"] | undefined>();
	const [currentThreadIndex, setCurrentThreadIndex] = React.useState(0);

	const [refetchKey, setRefetchKey] = React.useState(0);
	const refetchThread = React.useCallback(() => {
		setRefetchKey(v => v + 1);
	}, []);

	const currentThread = React.useMemo(
		() => currentThreads?.commentThreads?.[currentThreadIndex],
		[currentThreads, currentThreadIndex],
	);
	const canDelete = React.useMemo(
		() => currentThread?.access?.addedBy?.id === user?.id || organizerId === user?.id,
		[currentThread, organizerId, user],
	);

	const baseGroupKey = React.useMemo(() => `comments.${threadKey.split(":").join("-")}`, [threadKey]);

	const groupKeys = React.useMemo(() => {
		const keys = [baseGroupKey];
		if (currentThreads?.commentThreads) {
			currentThreads.commentThreads.forEach(thread => {
				if (thread) {
					keys.push(`${baseGroupKey}|${thread.id}`);
				}
			});
		}
		return keys;
	}, [baseGroupKey, currentThreads?.commentThreads]);

	const threads = useLazyLoadQuery<CommentsQuery>(
		threadsQuery,
		{
			key: threadKey,
		},
		{
			fetchPolicy: "store-and-network",
			fetchKey: refetchKey,
		},
	);

	const deleteComment = React.useCallback(() => {
		const count = currentThread?.comments.filter(c => !c.isDeleted).length;
		// if there is only one comment in the thread, it means it was the last deleted comment
		// so we close the panel given the thread was also deleted
		if (count === 1) {
			closeCommentPanel();
			return;
		}

		refetchThread();
	}, [refetchThread, closeCommentPanel, currentThread]);

	const allowedUsers = React.useMemo(() => {
		if (newCommentAccessType === "INDIVIDUALS" && newCommentRecipientUser) {
			return [
				{
					id: newCommentRecipientUser.id,
					fullName: newCommentRecipientUser.fullName,
					email: newCommentRecipientUser.email,
				},
			];
		}

		if (!currentThread) return noteUsers;
		if (currentThread.access?.accessType === "EVERYONE") return noteUsers;
		if (currentThread.access?.accessType === "INDIVIDUALS") {
			let users = currentThread.access.users;
			if (currentThread.access.addedBy) {
				users = [...users, currentThread.access.addedBy];
			}
			return users.map(u => ({
				id: u.id,
				fullName: u.fullName,
				email: u.email,
			}));
		}
		return [];
	}, [currentThread, noteUsers, newCommentAccessType, newCommentRecipientUser]);

	useEffect(() => {
		if (threads) {
			// set index to 0 only if count of threads is different (it means one thread is deleted or added)
			// when refetching after a comment deletion we don't want to move to index 0 since the number of threads is the same
			if (threads.commentThreads?.length !== currentThreads?.commentThreads?.length) {
				setCurrentThreadIndex(0);
			}
			setCurrentThreads(threads);
		}
		// set the current threads length in the array dependency to avoid infinite re-renders with setCurrentThreads
	}, [currentThreads?.commentThreads?.length, threads]);

	const updateGroupMetadata = React.useCallback(
		(override: boolean) => {
			groupKeys.forEach(groupKey => {
				WebsocketClient.getInstance().updateGroupMetadata(groupKey, override, {
					lastEntityId: Date.now().toString(),
				});
			});
		},
		[groupKeys],
	);

	const onWebsocketMessage = React.useCallback(
		(data: CommentOperationWebsocketMessage) => {
			if (data.type === "COMMENTS_UPDATED") {
				const commentData = data.data.comment;
				updateGroupMetadata(true);
				setCurrentThreads(currentResult => {
					if (!currentResult) return;
					const newComment = commentData;
					const threads = currentResult.commentThreads;
					if (!threads || !newComment) return currentResult;

					let threadToChange;
					const threadToChangeIndex = threads.findIndex(t => t.id === newComment.thread?.id);
					if (threadToChangeIndex === -1) {
						threadToChange = {
							...commentData.thread,
							comments: [],
						};
					} else {
						threadToChange = threads[threadToChangeIndex];
					}

					const firstThreads = threads.slice(0, threadToChangeIndex);
					const lastThreads = threads.slice(threadToChangeIndex + 1);

					const existingThreadComments = threadToChange.comments;
					const existingCommentIndex = existingThreadComments.findIndex(c => c.id === newComment.id);
					let newComments = [...existingThreadComments];
					if (existingCommentIndex > -1) {
						newComments.splice(existingCommentIndex, 1, newComment);
					} else {
						newComments.push(newComment);
					}

					return {
						commentThreads: [
							...firstThreads,
							{
								...threadToChange,
								...newComment.thread,
								comments: newComments,
							},
							...lastThreads,
						],
						viewer: currentResult.viewer,
					};
				});
			}
		},
		[updateGroupMetadata],
	);

	useEffect(() => {
		const sockets = groupKeys.map(groupKey => {
			const socket = WebsocketClient.getInstance().getOrJoinGroup(groupKey, {});
			socket.on("message", onWebsocketMessage);
			socket.on("open", () => {
				updateGroupMetadata(false);
			});
			return socket;
		});

		return () => {
			sockets.forEach(socket => {
				socket.off("message", onWebsocketMessage);
				socket.close();
			});
		};
	}, [groupKeys, onWebsocketMessage, updateGroupMetadata]);

	useEffect(() => {
		window.addEventListener("keydown", virtualKeyboardDetector.onKeyDown);
		window.addEventListener("keyup", virtualKeyboardDetector.onKeyUp);
		return () => {
			window.removeEventListener("keydown", virtualKeyboardDetector.onKeyDown);
			window.removeEventListener("keyup", virtualKeyboardDetector.onKeyUp);
		};
	}, [virtualKeyboardDetector]);

	const clearText = React.useCallback(() => {
		setContent(cloneDeep(EMPTY_DOC));
		setClearCount(v => v + 1);
	}, []);

	const setCurrentThreadDown = React.useCallback(() => {
		setCurrentThreadIndex(v => v - 1);
	}, []);

	const setCurrentThreadUp = React.useCallback(() => {
		setCurrentThreadIndex(v => v + 1);
	}, []);

	const onSubmit = React.useCallback(() => {
		if (!(content instanceof ProseNode) || isLoading) return;

		if (!hasAccessToRecapComments) {
			showFeatureModal("recap_comments");
			return;
		}

		const threadKey = threadKeyFromRecapIdAndTimes(
			recordingId,
			selectedCommentTimestampStart,
			selectedCommentTimestampEnd,
		);
		const text = content.textBetween(1, content.nodeSize - 2, "\n", "\n");
		setIsLoading(true);
		const commonParams = { threadKey, threadId: currentThread?.id, text, json: content.toJSON() };
		let params;
		if (!currentThread) {
			params = {
				...commonParams,
				accessType: newCommentAccessType,
				userIds: newCommentRecipientUser ? [newCommentRecipientUser.id] : undefined,
			};
		} else {
			params = commonParams;
		}

		const visibility = newCommentAccessType === "INDIVIDUALS" ? "individial" : "everyone";
		let numberOfTaggedUsers = 0;
		const editorView = editorRef.current?.editorView;
		if (editorView) {
			const editorState = editorView.state;
			if (editorState) {
				const typeaheadState = TypeAheadPluginKey.getState(editorState);
				if (typeaheadState) {
					numberOfTaggedUsers = typeaheadState.results.length;
					editorView.dispatch(editorView.state.tr.setMeta(TypeAheadPluginKey, resetState(typeaheadState)));
				}
			}
		}

		createOrUpdateComment(params)
			.then(() => {
				dispatchAnalyticsTrack("Comment created", { location: "Recap", visibility, tagsCount: numberOfTaggedUsers });
				clearText();
				refetchThread();
			})
			.finally(() => {
				setIsLoading(false);
			})
			.catch(() => {
				dispatchAnalyticsTrack("Error on comment creation", {
					location: "Recap",
					visibility,
					tagsCount: numberOfTaggedUsers,
				});
			});
	}, [
		content,
		currentThread,
		isLoading,
		recordingId,
		selectedCommentTimestampStart,
		selectedCommentTimestampEnd,
		hasAccessToRecapComments,
		clearText,
		newCommentAccessType,
		newCommentRecipientUser,
		refetchThread,
	]);

	const reopenPressed = React.useCallback(() => {
		if (actionLoading || !currentThread) {
			return;
		}

		setActionLoading(true);

		commitMutation<CommentsReopenThreadMutation>(environment, {
			mutation: reopenThreadMutation,
			variables: { id: currentThread.id },
			onCompleted: () => {
				setActionLoading(false);

				toast.success(<p>You can view the comments in the recap transcript.</p>, {
					title: "Thread reopened",
					closeButton: true,
				});
			},
		});
	}, [actionLoading, currentThread]);

	const onCopyClick = React.useCallback(() => {
		const url = currentThread?.fullUrl || "";
		copyToClipboard(url);
	}, [currentThread]);

	const resolvePressed = React.useCallback(() => {
		if (actionLoading || !currentThread) {
			return;
		}

		setActionLoading(true);

		commitMutation<CommentsResolveThreadMutation>(environment, {
			mutation: resolveThreadMutation,
			variables: { id: currentThread.id },
			onCompleted: () => {
				setActionLoading(false);
				closeCommentPanel();

				const toastId = toast.success(<p>You can view resolved comments from the note menu.</p>, {
					title: "Thread resolved",
					closeButton: true,
					actions: (
						<>
							<ToastActionButton onClick={handleReopenClick}>Reopen</ToastActionButton>
						</>
					),
				});

				function handleReopenClick() {
					toast.dismiss(toastId);
					reopenPressed();
				}

				const unlisten = window.HISTORY.listen(() => {
					unlisten();
					toast.dismiss(toastId);
				});
			},
		});
	}, [closeCommentPanel, actionLoading, currentThread, reopenPressed]);

	const deletePressed = React.useCallback(async () => {
		if (actionLoading || !currentThread || !canDelete) {
			return;
		}

		let message = (
			<div>
				Are you sure you want to delete this comment thread including{" "}
				{`${currentThread.comments.length} ${nGetText(currentThread.comments.length, "comment")}`}?
			</div>
		);

		const wannaDelete = await confirm(message, {
			yesText: "Yes, delete",
			title: "Delete comment thread?",
			yesProps: { variant: "destructive" },
		});
		if (!wannaDelete) {
			return;
		}

		setActionLoading(true);

		commitMutation<TranscriptCommentsPopoverDeleteThreadMutation>(environment, {
			mutation: deleteThreadMutation,
			variables: { id: currentThread.id },
			updater: store => {
				const thread = store.get(currentThread.id);
				if (thread) {
					store.delete(currentThread.id);
				}

				// closeCommentPanel unmounts the component
				// so we have to update the store before calling it
				// it can't be moved into onCompleted, because it's called before updater
				setActionLoading(false);
				closeCommentPanel();
			},
			onCompleted: () => {
				toast.success(<p>Comment thread was deleted successfully.</p>, {
					closeButton: true,
				});
			},
		});
	}, [currentThread, actionLoading, canDelete, closeCommentPanel]);

	const headerText = React.useMemo(() => {
		if (!currentThread) return "";
		if (
			currentThread.access &&
			currentThread.access.accessType === "INDIVIDUALS" &&
			currentThread.access.users.length > 0
		) {
			const accessUser = currentThread.access.users.find(u => u.id === user?.id);
			if (accessUser) {
				return `Visible to ${currentThread.access.addedBy?.fullName} and you`;
			} else {
				return `Visible to ${currentThread.access.users[0].fullName} and you`;
			}
		}
		return "Visible to everyone";
	}, [currentThread, user]);

	if (currentThread && currentThreads) {
		return (
			<div className={styles.existingCommentsPanel}>
				<div className={styles.existingCommentsHeader}>
					{(currentThreads.commentThreads?.length ?? 0) > 1 && (
						<div className={styles.iconsWrapper}>
							<IconButton
								disabled={currentThreadIndex === 0}
								icon={faAngleDown}
								className={styles.iconButton}
								onClick={setCurrentThreadDown}
							/>
							<IconButton
								disabled={currentThreadIndex === (currentThreads?.commentThreads?.length ?? 0) - 1}
								icon={faAngleUp}
								className={styles.iconButton}
								onClick={setCurrentThreadUp}
							/>
						</div>
					)}

					<div className={styles.visibleToInvitees}>{headerText}</div>
					<div className={styles.iconsWrapper}>
						<Menu key="more-actions-menu" mobileHeaderTitle="More actions">
							<Menu.Trigger>
								<IconButton icon={faEllipsisV} className={styles.iconButton} />
							</Menu.Trigger>
							<Menu.Content align="end">
								<Menu.Item icon={faLink} onSelect={onCopyClick}>
									Copy link
								</Menu.Item>
								<Menu.Item icon={faCheck} onSelect={resolvePressed}>
									Resolve
								</Menu.Item>

								{canDelete && (
									<>
										<Menu.Separator />
										<Menu.Item destructive icon={faTrashCan} onSelect={deletePressed}>
											Delete
										</Menu.Item>
									</>
								)}
							</Menu.Content>
						</Menu>

						<IconButton icon={faTimes} className={styles.iconButton} onClick={closeCommentPanel} />
					</div>
				</div>

				<div className={styles.existingCommentsSubPanel}>
					<div className={styles.existingCommentsList}>
						{currentThread.comments.map(comment => (
							<Comment
								key={`comment-${comment.id}`}
								comment={comment}
								currentUser={threads?.viewer}
								threadKey={currentThread.key || ""}
								threadId={currentThread.id}
								note={{ id: noteId }}
								onDeletedCompleted={deleteComment}
							/>
						))}
					</div>

					<div className={styles.replyCommentWrapper}>
						<div className={styles.addCommentForm}>
							{user && <Avatar user={user} size="small" className={styles.avatarWrapper} />}

							<div className={styles.newCommentEditorWrapper}>
								<CommentsEditor
									key={clearCount}
									onChange={setContent}
									onSubmit={onSubmit}
									doc={content}
									placeholder="Add a reply…"
									configRef={editorConfigRef}
									editorRef={editorRef}
									note={{ id: noteId }}
									virtualKeyboardDetector={virtualKeyboardDetector}
									className={styles.newCommentEditor}
									usersToMention={allowedUsers}
								/>
							</div>

							<Button
								variant={hasAccessToRecapComments ? "primary" : "tertiary"}
								className={styles.addCommentButton}
								onClick={onSubmit}
								disabled={!(content instanceof ProseNode && !!content.textContent) || isLoading}
							>
								{hasAccessToRecapComments ? <FontAwesomeIcon fixedWidth icon={faArrowUp} /> : <UpgradeIcon />}
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.newCommentPanel}>
			<div className={styles.newCommentPanelHeader}>
				<div className={styles.title}>Send comment to:</div>

				<TranscriptCommentsRecipientSelector
					accessType={newCommentAccessType}
					setAccessType={setNewCommentAccessType}
					selectedUser={newCommentRecipientUser}
					setSelectedUser={setNewCommentRecipientUser}
					noteUsers={noteUsers}
					hasAccessToPrivateRecapComments={hasAccessToPrivateRecapComments}
				/>
			</div>

			<div className={styles.editorWrapper}>
				<CommentsEditor
					key={clearCount}
					onChange={setContent}
					onSubmit={onSubmit}
					doc={content}
					placeholder="Add a comment…"
					configRef={editorConfigRef}
					editorRef={editorRef}
					note={{ id: noteId }}
					virtualKeyboardDetector={virtualKeyboardDetector}
					usersToMention={allowedUsers}
				/>
			</div>

			<div className={styles.buttonsRow}>
				<Button size="slim" variant="tertiary" onClick={closeCommentPanel}>
					Cancel
				</Button>

				<Button
					size="slim"
					variant="primary"
					onClick={onSubmit}
					disabled={!(content instanceof ProseNode && !!content.textContent) || isLoading}
				>
					Comment
				</Button>
			</div>
		</div>
	);
}

function LoaderFallback() {
	return (
		<div className={cx(styles.newCommentPanel, styles.centered)}>
			<LoadingSpinner dimmer={false} inline />
		</div>
	);
}

const CONTAINER_MAX_WIDTH_PX = 350;

export default React.forwardRef<HTMLDivElement, TranscriptCommentsPopoverProps>(
	function TranscriptCommentsPopoverWrapper({ uxSimplificationEnabled, ...props }, ref) {
		const { narrowViewport } = props;
		const [isNarrowWidthContainer, setIsNarrowWidthContainer] = React.useState(false);
		const wrapperElement = React.useMemo(
			() => document.getElementById(`transcript-modal-preview-${props.noteId}`),
			[props.noteId],
		);

		const positionProps: { side: (typeof SIDE_OPTIONS)[number]; align: (typeof ALIGN_OPTIONS)[number] } = narrowViewport
			? {
					side: "bottom",
					align: "end",
				}
			: {
					side: uxSimplificationEnabled ? "left" : "right",
					align: "start",
				};

		const { ref: resizeRef } = useResizeObserver<HTMLDivElement>({
			onResize: useMemo(
				() =>
					throttle(({ width }) => {
						// considering each Avatar width is 32px (minus 4px for overlapping)
						// we want to render the amount avatars that fit in `ref`
						if (!width) return;

						setIsNarrowWidthContainer(width < CONTAINER_MAX_WIDTH_PX);
					}, 200),
				[],
			),
		});

		const contentRefCallback = React.useCallback(
			(element: HTMLDivElement | null) => {
				// Forward the ref to the parent component
				if (typeof ref === "function") {
					ref(element);
				} else if (ref) {
					ref.current = element;
				}
			},
			[ref],
		);

		return (
			<Popover.Root open>
				<Popover.Anchor ref={resizeRef} />
				<Popover.Portal>
					<Popover.Content
						{...positionProps}
						className={cx(styles.popoverContent, { [styles.narrowPopoverContent]: isNarrowWidthContainer })}
						collisionBoundary={wrapperElement}
						ref={contentRefCallback}
					>
						<React.Suspense fallback={<LoaderFallback />}>
							<TranscriptCommentsPopover {...props} />
						</React.Suspense>
					</Popover.Content>
				</Popover.Portal>
			</Popover.Root>
		);
	},
);
